import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  Loader2, ChevronLeft, RefreshCcw, Copy, ExternalLink, Check, X,
  Building2, MapPin, Globe, Phone, Star, Clock, Camera, ShieldCheck, Info, AlertCircle,
  Sparkles, ArrowUpDown, FileText, Beaker, Bot,
} from "lucide-react";
import {
  getBusinessListingDetail, refreshBusinessListing, previewSelection, getAdminToken,
  composeBriefDraft, regenerateBriefComposition, getLatestBriefDraft, getBriefComposition,
  createBriefRendering, listBriefRenderings, getBriefRendering, approveBriefRendering,
  sendTestBriefRendering, sendNowBriefRendering, scheduleBriefRendering,
  listBriefRenderingDeliveries, cancelDelivery, retryDeliveryV2, listDeliveryEvents,
  getAdminAiDiscoveryLatest, refreshAdminAiDiscovery,
  getAdminWebsiteIntelligence, scanAdminWebsiteIntelligence,
} from "@/lib/api";
import { toast } from "sonner";
import AdminHeader from "@/components/AdminHeader";
import AdminGate from "@/components/AdminGate";
import { formatApiErrorForToast } from "@/lib/apiErrorMessages";

export default function AdminBusinessDetail() {
  const [authed, setAuthed] = useState(!!getAdminToken());
  const { id } = useParams();
  if (!authed) return <AdminGate onAuthed={() => setAuthed(true)} redirect={`/admin/businesses/${id}`} />;
  return <AdminBusinessDetailInner id={id} />;
}

function AdminBusinessDetailInner({ id }) {
  const [busy, setBusy] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [data, setData] = useState(null);
  const [showRaw, setShowRaw] = useState(false);
  const [obsStatusFilter, setObsStatusFilter] = useState("active");

  const load = async (opts = {}) => {
    setBusy(true);
    try {
      const d = await getBusinessListingDetail(id, opts.statusFilter || obsStatusFilter);
      setData(d);
    } catch (err) {
      toast.error("Couldn't load listing detail.", { description: err?.message });
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { load(); }, [id, obsStatusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const onRefresh = async () => {
    setRefreshing(true);
    try {
      const res = await refreshBusinessListing(id);
      toast.success("Listing refreshed.", {
        description: `${res.candidatesGenerated} observation candidates · ${res.changes?.length ?? 0} field change(s).`,
      });
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const code = typeof detail === "object" ? detail?.code : null;
      const msg = (typeof detail === "object" ? detail?.message : detail) || err?.message || "Try again in a moment.";
      if (code === "no_place_id") {
        toast.error("No Google listing yet.", { description: msg });
      } else if (code === "discovery_disabled" || code === "provider_unavailable") {
        toast.error("Discovery unavailable.", { description: msg });
      } else {
        toast.error("Refresh failed.", { description: msg });
      }
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: "var(--ta-bg)" }} data-testid="admin-business-detail-page">
      <AdminHeader onRefresh={load} refreshing={busy} active="businesses" />

      <main className="w-full max-w-screen-lg mx-auto px-5 sm:px-8 py-6 page-fade">
        <Link
          to="/admin/businesses"
          data-testid="admin-business-back"
          className="inline-flex items-center gap-1 text-sm font-medium mb-3"
          style={{ color: "var(--ta-primary)" }}
        >
          <ChevronLeft size={16} /> All businesses
        </Link>

        {busy && !data && (
          <div className="mt-6 flex items-center gap-2 text-sm" style={{ color: "var(--ta-text-secondary)" }}>
            <Loader2 size={16} className="animate-spin" /> Loading…
          </div>
        )}

        {data && (
          <>
            <Header business={data.business} onRefresh={onRefresh} refreshing={refreshing} />

            {!data.normalizedGoogleListing ? (
              <ManualNotice source={data.business?.discoverySource} />
            ) : (
              <ListingSection normalized={data.normalizedGoogleListing} business={data.business} />
            )}

            <CandidatesSection
              candidates={data.openObservationCandidates || []}
              staleCount={data.staleObservationCandidateCount || 0}
            />

            <ObservationIntelligenceSection
              observations={data.businessObservations || []}
              validation={data.ruleValidation}
              statusLog={data.observationStatusLog || []}
              statusFilter={obsStatusFilter}
              onStatusFilterChange={setObsStatusFilter}
            />

            <RecommendationsSection recommendations={data.recommendations || []} />

            <AiDiscoverySection businessId={id} />

            <WebsiteIntelligenceSection businessId={id} />

            <PreviewSelectionPanel businessId={id} />

            <BriefCompositionSection businessId={id} />

            <BriefDeliverySection businessId={id} />

            <ChangeLogSection log={data.changeLog || []} />

            <RawSection
              raw={data.rawGoogleListing}
              show={showRaw}
              onToggle={() => setShowRaw((v) => !v)}
            />
          </>
        )}
      </main>
    </div>
  );
}

const Header = ({ business, onRefresh, refreshing }) => {
  const hasPlaceId = !!business?.googlePlaceId;
  return (
    <div className="mt-2 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
      <div className="min-w-0">
        <div className="text-xs uppercase tracking-widest font-semibold" style={{ color: "var(--ta-secondary)" }}>
          Business
        </div>
        <h1
          className="font-display text-3xl sm:text-4xl font-semibold tracking-tight mt-1 truncate"
          style={{ color: "var(--ta-primary)" }}
          data-testid="admin-business-name"
        >
          {business?.businessName || "—"}
        </h1>
        {business?.ownerEnteredBusinessName && business.ownerEnteredBusinessName !== business.businessName && (
          <div className="text-sm mt-1" style={{ color: "var(--ta-text-secondary)" }}>
            Owner entered: <span className="font-medium">{business.ownerEnteredBusinessName}</span>
          </div>
        )}
      </div>
      <button
        onClick={onRefresh}
        disabled={refreshing || !hasPlaceId}
        data-testid="admin-business-refresh-listing"
        className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-full text-sm font-medium text-white disabled:opacity-40"
        style={{ background: "var(--ta-primary)" }}
      >
        <RefreshCcw size={15} className={refreshing ? "animate-spin" : ""} />
        {refreshing ? "Refreshing…" : "Refresh Google Business Listing"}
      </button>
    </div>
  );
};

const ManualNotice = ({ source }) => (
  <div
    className="mt-6 p-5 rounded-2xl border-2 flex items-start gap-3"
    style={{ borderColor: "var(--ta-border)", background: "#fff" }}
  >
    <AlertCircle size={20} style={{ color: "var(--ta-primary)" }} className="mt-0.5 shrink-0" />
    <div>
      <div className="font-display font-semibold text-base" style={{ color: "var(--ta-primary)" }}>
        No Google Business Listing on file.
      </div>
      <p className="text-sm mt-1" style={{ color: "var(--ta-text-secondary)" }}>
        This business was added via <span className="font-medium">{source || "manual entry"}</span>. Google-derived fields, categories, hours and photos are not available until a Place ID is confirmed.
      </p>
    </div>
  </div>
);

const ListingSection = ({ normalized: n, business: b }) => {
  const address = n.formattedAddress || [n.street, n.city, n.state, n.zipCode].filter(Boolean).join(", ");
  return (
    <section
      className="mt-6 rounded-2xl border-2 bg-white overflow-hidden"
      style={{ borderColor: "var(--ta-border)" }}
      data-testid="admin-google-listing"
    >
      <SectionHeader
        icon={Building2}
        eyebrow="Section"
        title="Google Business Listing"
        rightSlot={<StatusPill status={n.businessStatus} />}
      />
      <div className="px-5 sm:px-6 py-5 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-5">
        <Field label="Business Name" value={n.googleBusinessName} testId="field-business-name" />
        <Field
          label="Google Place ID"
          value={n.placeId}
          mono
          copyable
          testId="field-place-id"
        />
        <Field
          label="Maps Link"
          value={n.mapsUrl ? (
            <a
              href={n.mapsUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 underline"
              style={{ color: "var(--ta-primary)" }}
              data-testid="field-maps-link"
            >
              Open in Google Maps <ExternalLink size={13} />
            </a>
          ) : null}
        />
        <Field label="Primary Category" value={n.primaryCategoryDisplay || humanize(n.primaryCategory)} testId="field-primary-category" />
        <Field
          label="Secondary Categories"
          value={(n.secondaryCategories && n.secondaryCategories.length)
            ? n.secondaryCategories.map(humanize).join(", ")
            : null}
          testId="field-secondary-categories"
        />
        <Field
          label="Rating"
          value={typeof n.rating === "number" ? (
            <span className="inline-flex items-center gap-1"><Star size={14} /> {n.rating.toFixed(1)}</span>
          ) : null}
          testId="field-rating"
        />
        <Field label="Review Count" value={typeof n.reviewCount === "number" ? n.reviewCount : null} testId="field-review-count" />
        <Field
          label="Website"
          value={n.websiteUrl ? (
            <a
              href={n.websiteUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 underline break-all"
              style={{ color: "var(--ta-primary)" }}
              data-testid="field-website"
            >
              {n.websiteDomain || n.websiteUrl.replace(/^https?:\/\//, "")} <ExternalLink size={13} />
            </a>
          ) : null}
        />
        <Field
          label="Phone"
          value={n.phoneDisplay || n.phoneE164}
          hint={n.phoneE164 && n.phoneDisplay ? n.phoneE164 : null}
          testId="field-phone"
        />
        <Field
          label="Address"
          value={address || null}
          testId="field-address"
          span="sm:col-span-2"
        />
        <Field
          label="Coordinates"
          value={(typeof n.latitude === "number" && typeof n.longitude === "number")
            ? `${n.latitude.toFixed(6)}, ${n.longitude.toFixed(6)}`
            : null}
          mono
          testId="field-coords"
        />
        <Field
          label="Currently Open"
          value={n.openNow === true ? "Yes" : n.openNow === false ? "No" : null}
          testId="field-open-now"
        />
        <div className="sm:col-span-2">
          <FieldLabel label="Regular Hours" icon={Clock} />
          <HoursBlock hours={n.regularHours} />
        </div>
        <Field
          label="Photo Count"
          value={typeof n.photoCount === "number" ? n.photoCount : null}
          testId="field-photo-count"
        />
        <Field
          label="Cover Photo Reference"
          value={n.coverPhotoRef}
          mono
          truncate
          testId="field-cover-ref"
        />
      </div>

      <div
        className="px-5 sm:px-6 py-4 border-t grid grid-cols-1 sm:grid-cols-3 gap-x-8 gap-y-3"
        style={{ borderColor: "var(--ta-border)", background: "var(--ta-surface-alt)" }}
      >
        <MetaField label="Last Refreshed" value={fmtDate(b.googleListingLastRefreshedAt)} />
        <MetaField
          label="Discovery Confidence"
          value={b.discoveryConfidenceScore != null
            ? `${b.discoveryConfidenceScore}/100${b.discoveryConfidenceLabel ? ` · ${b.discoveryConfidenceLabel}` : ""}`
            : "—"}
        />
        <MetaField label="Owner Confirmed" value={fmtDate(b.ownerConfirmedAt)} icon={b.ownerConfirmedAt ? ShieldCheck : null} />
      </div>
    </section>
  );
};

const OBS_SORT_OPTIONS = [
  { id: "attention",    label: "Attention" },
  { id: "recommendable", label: "Recommendable" },
  { id: "ownerValue",   label: "Owner Value" },
  { id: "priority",     label: "Priority" },
  { id: "businessArea", label: "Business Area" },
  { id: "intent",       label: "Intent" },
  { id: "confidence",   label: "Confidence" },
  { id: "freshness",    label: "Freshness" },
];

const PRIORITY_ORDER = { Critical: 0, High: 1, Medium: 2, Low: 3, Informational: 4 };
const CONFIDENCE_ORDER = { high: 0, medium: 1, low: 2, unknown: 3 };
const INTENT_ORDER = { Warning: 0, Opportunity: 1, Reassurance: 2, Informational: 3 };
const ATTENTION_ORDER = { Critical: 0, High: 1, Medium: 2, Low: 3, Ignore: 4 };
const OWNER_VALUE_ORDER = {
  "Protect Revenue": 0, "Reduce Risk": 1, "Improve Customer Experience": 2,
  "Improve Visibility": 3, "Build Trust": 4, "Operational Awareness": 5, "Reassurance": 6,
};

const ObservationIntelligenceSection = ({ observations, validation, statusLog, statusFilter, onStatusFilterChange }) => {
  const [sortBy, setSortBy] = useState("priority");

  const rows = useMemo(() => {
    const arr = [...(observations || [])];
    const byFreshness = (a, b) => (b.lastChangedAt || "").localeCompare(a.lastChangedAt || "");
    if (sortBy === "attention") {
      arr.sort((a, b) => {
        const pa = ATTENTION_ORDER[a.attentionLevel] ?? 99;
        const pb = ATTENTION_ORDER[b.attentionLevel] ?? 99;
        return pa - pb || byFreshness(a, b);
      });
    } else if (sortBy === "recommendable") {
      arr.sort((a, b) => {
        const ra = a.recommendationEligible ? 0 : 1;
        const rb = b.recommendationEligible ? 0 : 1;
        return ra - rb || (ATTENTION_ORDER[a.attentionLevel] ?? 99) - (ATTENTION_ORDER[b.attentionLevel] ?? 99);
      });
    } else if (sortBy === "ownerValue") {
      arr.sort((a, b) => {
        const va = OWNER_VALUE_ORDER[a.ownerValue] ?? 99;
        const vb = OWNER_VALUE_ORDER[b.ownerValue] ?? 99;
        return va - vb || byFreshness(a, b);
      });
    } else if (sortBy === "priority") {
      arr.sort((a, b) => {
        const pa = PRIORITY_ORDER[a.priority] ?? 99;
        const pb = PRIORITY_ORDER[b.priority] ?? 99;
        return pa - pb || byFreshness(a, b);
      });
    } else if (sortBy === "businessArea") {
      arr.sort((a, b) => (a.businessArea || "").localeCompare(b.businessArea || "") || byFreshness(a, b));
    } else if (sortBy === "intent") {
      arr.sort((a, b) => {
        const ia = INTENT_ORDER[a.intent] ?? 99;
        const ib = INTENT_ORDER[b.intent] ?? 99;
        return ia - ib || byFreshness(a, b);
      });
    } else if (sortBy === "confidence") {
      arr.sort((a, b) => {
        const ca = CONFIDENCE_ORDER[(a.confidence || "").toLowerCase()] ?? 99;
        const cb = CONFIDENCE_ORDER[(b.confidence || "").toLowerCase()] ?? 99;
        return ca - cb || byFreshness(a, b);
      });
    } else {
      arr.sort(byFreshness);
    }
    return arr;
  }, [observations, sortBy]);

  const validationClean =
    validation
    && (validation.unknownRuleKeys || []).length === 0
    && (validation.missingFieldsByKey || []).length === 0;

  return (
    <section
      className="mt-6 rounded-2xl border-2 bg-white overflow-hidden"
      style={{ borderColor: "var(--ta-border)" }}
      data-testid="admin-observation-intelligence"
    >
      <SectionHeader
        icon={Sparkles}
        eyebrow="Sprint 1B · Classified"
        title="Observation Intelligence"
        rightSlot={
          <span className="text-xs" style={{ color: "var(--ta-text-secondary)" }}>
            {(observations || []).length} total · rules {validation?.ruleVersion || "—"}
          </span>
        }
      />

      <div
        className="px-5 sm:px-6 py-3 border-b flex items-center gap-2 flex-wrap"
        style={{ borderColor: "var(--ta-border)", background: "var(--ta-surface-alt)" }}
      >
        <span className="text-xs uppercase tracking-widest font-semibold" style={{ color: "var(--ta-secondary)" }}>
          Show
        </span>
        {[
          { id: "active", label: "Active" },
          { id: "resolved", label: "Resolved" },
          { id: "all", label: "All history" },
        ].map((opt) => (
          <button
            key={opt.id}
            data-testid={`obs-status-filter-${opt.id}`}
            onClick={() => onStatusFilterChange && onStatusFilterChange(opt.id)}
            className="text-xs font-medium px-3 py-1 rounded-full transition-colors"
            style={{
              background: statusFilter === opt.id ? "var(--ta-primary)" : "#fff",
              color: statusFilter === opt.id ? "#fff" : "var(--ta-primary)",
              border: `1px solid ${statusFilter === opt.id ? "var(--ta-primary)" : "var(--ta-border)"}`,
            }}
          >
            {opt.label}
          </button>
        ))}
      </div>

      <div
        className="px-5 sm:px-6 py-3 border-b flex items-center gap-2 flex-wrap"
        style={{ borderColor: "var(--ta-border)", background: "var(--ta-surface-alt)" }}
      >
        <ArrowUpDown size={14} style={{ color: "var(--ta-text-secondary)" }} />
        <span className="text-xs uppercase tracking-widest font-semibold" style={{ color: "var(--ta-secondary)" }}>
          Sort by
        </span>
        {OBS_SORT_OPTIONS.map((opt) => (
          <button
            key={opt.id}
            data-testid={`obs-sort-${opt.id}`}
            onClick={() => setSortBy(opt.id)}
            className="text-xs font-medium px-3 py-1 rounded-full transition-colors"
            style={{
              background: sortBy === opt.id ? "var(--ta-primary)" : "#fff",
              color: sortBy === opt.id ? "#fff" : "var(--ta-primary)",
              border: `1px solid ${sortBy === opt.id ? "var(--ta-primary)" : "var(--ta-border)"}`,
            }}
          >
            {opt.label}
          </button>
        ))}
        <span className="ml-auto text-[11px] uppercase tracking-widest font-semibold" style={{ color: validationClean ? "var(--ta-success)" : "#C2571B" }} data-testid="obs-validation-status">
          {validationClean ? "Rules valid" : `${(validation?.unknownRuleKeys || []).length} unknown, ${(validation?.missingFieldsByKey || []).length} incomplete`}
        </span>
      </div>

      {rows.length === 0 ? (
        <div className="px-5 sm:px-6 py-6 text-sm" style={{ color: "var(--ta-text-secondary)" }}>
          No classified observations yet. They appear after the first refresh runs the engine.
        </div>
      ) : (
        <ul className="divide-y" style={{ borderColor: "var(--ta-border)" }} data-testid="obs-intel-list">
          {rows.map((o) => (
            <ObservationRow key={o.id} obs={o} />
          ))}
        </ul>
      )}

      {statusLog && statusLog.length > 0 && (
        <details className="border-t" style={{ borderColor: "var(--ta-border)" }} data-testid="obs-status-log">
          <summary className="px-5 sm:px-6 py-3 cursor-pointer text-sm font-medium" style={{ color: "var(--ta-primary)" }}>
            Recent lifecycle transitions ({statusLog.length})
          </summary>
          <ul className="divide-y max-h-96 overflow-y-auto" style={{ borderColor: "var(--ta-border)" }}>
            {statusLog.map((l) => (
              <li key={l.id} className="px-5 sm:px-6 py-2 text-xs font-mono flex flex-wrap items-center gap-x-3" style={{ color: "var(--ta-text)" }}>
                <span style={{ color: "var(--ta-text-secondary)" }}>{fmtDate(l.changedAt)}</span>
                <span style={{ color: "var(--ta-primary)" }}>{l.observationKey}</span>
                <span style={{ color: "var(--ta-text-secondary)" }}>{l.previousStatus || "—"} → {l.newStatus}</span>
                <span style={{ color: "var(--ta-secondary)" }}>{l.reason}</span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
};

const REC_SORT_OPTIONS = [
  { id: "category",   label: "Category" },
  { id: "impact",     label: "Impact" },
  { id: "confidence", label: "Confidence" },
  { id: "status",     label: "Status" },
];

const REC_CATEGORY_ORDER = { Fix: 0, Verify: 1, Improve: 2, Monitor: 3, Celebrate: 4 };
const REC_IMPACT_ORDER = { Large: 0, Medium: 1, Small: 2 };
const REC_STATUS_ORDER = { Proposed: 0, Approved: 1, Deferred: 2, Suppressed: 3, Completed: 4 };
const REC_CONFIDENCE_ORDER = { high: 0, medium: 1, low: 2, unknown: 3 };

const RecommendationsSection = ({ recommendations }) => {
  const [sortBy, setSortBy] = useState("category");
  const rows = useMemo(() => {
    const arr = [...(recommendations || [])];
    const byUpdatedDesc = (a, b) => (b.updatedAt || "").localeCompare(a.updatedAt || "");
    if (sortBy === "category") {
      arr.sort((a, b) =>
        (REC_CATEGORY_ORDER[a.recommendationCategory] ?? 99) -
        (REC_CATEGORY_ORDER[b.recommendationCategory] ?? 99) || byUpdatedDesc(a, b));
    } else if (sortBy === "impact") {
      arr.sort((a, b) =>
        (REC_IMPACT_ORDER[a.expectedBusinessImpact] ?? 99) -
        (REC_IMPACT_ORDER[b.expectedBusinessImpact] ?? 99) || byUpdatedDesc(a, b));
    } else if (sortBy === "confidence") {
      arr.sort((a, b) =>
        (REC_CONFIDENCE_ORDER[(a.recommendationConfidence || "").toLowerCase()] ?? 99) -
        (REC_CONFIDENCE_ORDER[(b.recommendationConfidence || "").toLowerCase()] ?? 99) || byUpdatedDesc(a, b));
    } else {
      arr.sort((a, b) =>
        (REC_STATUS_ORDER[a.status] ?? 99) -
        (REC_STATUS_ORDER[b.status] ?? 99) || byUpdatedDesc(a, b));
    }
    return arr;
  }, [recommendations, sortBy]);

  return (
    <section
      className="mt-6 rounded-2xl border-2 bg-white overflow-hidden"
      style={{ borderColor: "var(--ta-border)" }}
      data-testid="admin-recommendations"
    >
      <SectionHeader
        icon={Sparkles}
        eyebrow="Sprint 1D · Structured"
        title="Recommendations"
        rightSlot={
          <span className="text-xs" style={{ color: "var(--ta-text-secondary)" }}>
            {(recommendations || []).length} total
          </span>
        }
      />
      <div
        className="px-5 sm:px-6 py-3 border-b flex items-center gap-2 flex-wrap"
        style={{ borderColor: "var(--ta-border)", background: "var(--ta-surface-alt)" }}
      >
        <ArrowUpDown size={14} style={{ color: "var(--ta-text-secondary)" }} />
        <span className="text-xs uppercase tracking-widest font-semibold" style={{ color: "var(--ta-secondary)" }}>
          Sort by
        </span>
        {REC_SORT_OPTIONS.map((opt) => (
          <button
            key={opt.id}
            data-testid={`rec-sort-${opt.id}`}
            onClick={() => setSortBy(opt.id)}
            className="text-xs font-medium px-3 py-1 rounded-full transition-colors"
            style={{
              background: sortBy === opt.id ? "var(--ta-primary)" : "#fff",
              color: sortBy === opt.id ? "#fff" : "var(--ta-primary)",
              border: `1px solid ${sortBy === opt.id ? "var(--ta-primary)" : "var(--ta-border)"}`,
            }}
          >
            {opt.label}
          </button>
        ))}
      </div>
      {rows.length === 0 ? (
        <div className="px-5 sm:px-6 py-6 text-sm" style={{ color: "var(--ta-text-secondary)" }}>
          No recommendations yet. They generate automatically as recommendation-eligible observations appear.
        </div>
      ) : (
        <ul className="divide-y" style={{ borderColor: "var(--ta-border)" }} data-testid="rec-list">
          {rows.map((r) => (
            <RecommendationRow key={r.id} rec={r} />
          ))}
        </ul>
      )}
    </section>
  );
};

const RecommendationRow = ({ rec }) => (
  <li
    className="px-5 sm:px-6 py-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3"
    data-testid={`rec-row-${rec.reasonCode}`}
  >
    <RecStatusChip status={rec.status} />
    <code className="text-[13px] font-mono truncate" style={{ color: "var(--ta-primary)" }}>
      {rec.reasonCode}
    </code>
    <div className="flex flex-wrap items-center gap-1.5 sm:ml-auto">
      <PillBase bg="#F5E7DE" fg="#8A4A2D" data-testid={`rec-${rec.reasonCode}-category`}>
        {rec.recommendationCategory}
      </PillBase>
      <PillBase bg="var(--ta-accent)" fg="var(--ta-primary)" data-testid={`rec-${rec.reasonCode}-goal`}>
        {rec.businessGoal}
      </PillBase>
      <PillBase bg="var(--ta-surface-alt)" fg="var(--ta-primary)" data-testid={`rec-${rec.reasonCode}-impact`}>
        Impact: {rec.expectedBusinessImpact}
      </PillBase>
      <PillBase bg="var(--ta-surface-alt)" fg="var(--ta-primary)">
        Effort: {rec.ownerEffort}
      </PillBase>
      {rec.ownerCanHandle && (
        <PillBase bg="var(--ta-success-bg)" fg="var(--ta-success)" data-testid={`rec-${rec.reasonCode}-owner-can-handle`}>
          Owner
        </PillBase>
      )}
      {rec.growthPartnerCanHandle && (
        <PillBase bg="var(--ta-success-bg)" fg="var(--ta-success)" data-testid={`rec-${rec.reasonCode}-partner-can-handle`}>
          Growth Partner
        </PillBase>
      )}
      <PillConfidence confidence={rec.recommendationConfidence} />
    </div>
  </li>
);

const RecStatusChip = ({ status }) => {
  const map = {
    Proposed:   { bg: "var(--ta-accent)",     fg: "var(--ta-primary)" },
    Approved:   { bg: "var(--ta-success-bg)", fg: "var(--ta-success)" },
    Deferred:   { bg: "var(--ta-surface-alt)", fg: "var(--ta-text-secondary)" },
    Suppressed: { bg: "var(--ta-surface-alt)", fg: "var(--ta-text-secondary)" },
    Completed:  { bg: "var(--ta-success-bg)", fg: "var(--ta-success)" },
  };
  const c = map[status] || map.Proposed;
  return (
    <span
      className="text-[11px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-full shrink-0"
      style={{ background: c.bg, color: c.fg }}
      data-testid={`rec-status-${status}`}
    >
      {status}
    </span>
  );
};


const ObservationRow = ({ obs }) => (
  <li
    className="px-5 sm:px-6 py-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3"
    data-testid={`obs-row-${obs.observationKey}`}
  >
    <StatusChip status={obs.status} />
    <code className="text-[13px] font-mono truncate" style={{ color: "var(--ta-primary)" }}>
      {obs.observationKey}
    </code>
    <div className="flex flex-wrap items-center gap-1.5 sm:ml-auto">
      {obs.attentionLevel && (
        <PillAttention level={obs.attentionLevel} data-testid={`obs-${obs.observationKey}-attention`} />
      )}
      {obs.recommendationEligible ? (
        <PillBase bg="var(--ta-accent)" fg="var(--ta-primary)" data-testid={`obs-${obs.observationKey}-rec-eligible`}>
          Recommendable
        </PillBase>
      ) : null}
      {obs.ownerValue && (
        <PillBase bg="var(--ta-surface-alt)" fg="var(--ta-primary)" data-testid={`obs-${obs.observationKey}-owner-value`}>
          {obs.ownerValue}
        </PillBase>
      )}
      {obs.explanationCode && (
        <code
          className="text-[10px] font-mono px-2 py-0.5 rounded-full whitespace-nowrap"
          style={{ background: "var(--ta-surface-alt)", color: "var(--ta-text-secondary)" }}
          data-testid={`obs-${obs.observationKey}-explanation-code`}
          title={`Repeat: ${obs.repeatBehavior || "—"} · Escalation: ${obs.escalationPolicy || "—"}`}
        >
          {obs.explanationCode}
        </code>
      )}
      <PillNeutral>{obs.observationType}</PillNeutral>
      <PillAccent data-testid={`obs-${obs.observationKey}-area`}>{obs.businessArea}</PillAccent>
      <PillIntent intent={obs.intent} />
      <PillPriority priority={obs.priority} data-testid={`obs-${obs.observationKey}-priority`} />
      <PillConfidence confidence={obs.confidence} />
    </div>
  </li>
);

const PillAttention = ({ level, ...rest }) => {
  const map = {
    Critical: { bg: "#F5D7C6", fg: "#7A2B0F" },
    High:     { bg: "#F5E7DE", fg: "#8A4A2D" },
    Medium:   { bg: "var(--ta-accent)", fg: "var(--ta-primary)" },
    Low:      { bg: "var(--ta-surface-alt)", fg: "var(--ta-primary)" },
    Ignore:   { bg: "var(--ta-surface-alt)", fg: "var(--ta-text-secondary)" },
  };
  const c = map[level] || map.Low;
  return <PillBase bg={c.bg} fg={c.fg} {...rest}>Attn: {level}</PillBase>;
};

const StatusChip = ({ status }) => {
  const map = {
    New:       { bg: "var(--ta-accent)",     fg: "var(--ta-primary)" },
    Confirmed: { bg: "var(--ta-success-bg)", fg: "var(--ta-success)" },
    Changed:   { bg: "#F5E7DE",              fg: "#8A4A2D" },
    Resolved:  { bg: "var(--ta-surface-alt)", fg: "var(--ta-text-secondary)" },
    Stale:     { bg: "var(--ta-surface-alt)", fg: "var(--ta-text-secondary)" },
  };
  const c = map[status] || { bg: "var(--ta-surface-alt)", fg: "var(--ta-text-secondary)" };
  return (
    <span
      className="text-[11px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-full shrink-0"
      style={{ background: c.bg, color: c.fg }}
      data-testid={`obs-status-${status}`}
    >
      {status}
    </span>
  );
};

const PillBase = ({ children, bg, fg, ...rest }) => (
  <span
    className="text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-full whitespace-nowrap"
    style={{ background: bg, color: fg }}
    {...rest}
  >
    {children}
  </span>
);
const PillNeutral = ({ children, ...rest }) => (
  <PillBase bg="var(--ta-surface-alt)" fg="var(--ta-primary)" {...rest}>{children}</PillBase>
);
const PillAccent = ({ children, ...rest }) => (
  <PillBase bg="var(--ta-accent)" fg="var(--ta-primary)" {...rest}>{children}</PillBase>
);
const PillIntent = ({ intent }) => {
  const map = {
    Warning:       { bg: "#F5E7DE", fg: "#8A4A2D" },
    Opportunity:   { bg: "var(--ta-accent)", fg: "var(--ta-primary)" },
    Reassurance:   { bg: "var(--ta-success-bg)", fg: "var(--ta-success)" },
    Informational: { bg: "var(--ta-surface-alt)", fg: "var(--ta-text-secondary)" },
  };
  const c = map[intent] || map.Informational;
  return <PillBase bg={c.bg} fg={c.fg}>{intent}</PillBase>;
};
const PillPriority = ({ priority, ...rest }) => {
  const map = {
    Critical:      { bg: "#F5E7DE", fg: "#8A4A2D" },
    High:          { bg: "#F5E7DE", fg: "#8A4A2D" },
    Medium:        { bg: "var(--ta-accent)", fg: "var(--ta-primary)" },
    Low:           { bg: "var(--ta-surface-alt)", fg: "var(--ta-primary)" },
    Informational: { bg: "var(--ta-surface-alt)", fg: "var(--ta-text-secondary)" },
  };
  const c = map[priority] || map.Informational;
  return <PillBase bg={c.bg} fg={c.fg} {...rest}>P: {priority}</PillBase>;
};
const PillConfidence = ({ confidence }) => {
  const map = {
    high:    { bg: "var(--ta-success-bg)", fg: "var(--ta-success)" },
    medium:  { bg: "var(--ta-accent)",     fg: "var(--ta-primary)" },
    low:     { bg: "#F5F1EA",              fg: "#7A6A56" },
    unknown: { bg: "var(--ta-surface-alt)", fg: "var(--ta-text-secondary)" },
  };
  const c = map[(confidence || "").toLowerCase()] || map.unknown;
  return <PillBase bg={c.bg} fg={c.fg}>Conf: {confidence}</PillBase>;
};
const PillImpact = ({ impact }) => {
  const map = {
    High:    { bg: "#F5E7DE", fg: "#8A4A2D" },
    Medium:  { bg: "var(--ta-accent)", fg: "var(--ta-primary)" },
    Low:     { bg: "var(--ta-surface-alt)", fg: "var(--ta-primary)" },
    Unknown: { bg: "var(--ta-surface-alt)", fg: "var(--ta-text-secondary)" },
  };
  const c = map[impact] || map.Unknown;
  return <PillBase bg={c.bg} fg={c.fg}>Impact: {impact}</PillBase>;
};
const PillVisibility = ({ visibility }) => {
  const map = {
    "Always":       { bg: "var(--ta-success-bg)", fg: "var(--ta-success)" },
    "Conditional":  { bg: "var(--ta-accent)",     fg: "var(--ta-primary)" },
    "Internal Only":{ bg: "var(--ta-surface-alt)", fg: "var(--ta-text-secondary)" },
  };
  const c = map[visibility] || map["Internal Only"];
  return <PillBase bg={c.bg} fg={c.fg}>Vis: {visibility}</PillBase>;
};


const CandidatesSection = ({ candidates, staleCount }) => (
  <section
    className="mt-6 rounded-2xl border-2 bg-white overflow-hidden"
    style={{ borderColor: "var(--ta-border)" }}
    data-testid="admin-observation-candidates"
  >
    <SectionHeader
      icon={Info}
      eyebrow="Facts, not advice yet"
      title="Observation Candidates"
      rightSlot={
        <span className="text-xs" style={{ color: "var(--ta-text-secondary)" }}>
          {candidates.length} open{staleCount ? ` · ${staleCount} stale` : ""}
        </span>
      }
    />
    {candidates.length === 0 ? (
      <div className="px-5 sm:px-6 py-6 text-sm" style={{ color: "var(--ta-text-secondary)" }}>
        No candidates yet. They appear after the first confirm or refresh.
      </div>
    ) : (
      <ul className="divide-y" style={{ borderColor: "var(--ta-border)" }}>
        {candidates.map((c, i) => (
          <li
            key={c.id || `${c.kind}-${i}`}
            className="px-5 sm:px-6 py-3 flex items-center gap-3 flex-wrap"
            data-testid={`candidate-${c.kind}`}
          >
            <SeverityDot severity={c.severity} />
            <code
              className="text-[13px] font-mono"
              style={{ color: "var(--ta-primary)" }}
            >
              {c.kind}
            </code>
            {c.observationType && (
              <span
                className="text-[11px] font-semibold px-2 py-0.5 rounded-full"
                style={{ background: "var(--ta-surface-alt)", color: "var(--ta-primary)" }}
                data-testid={`candidate-${c.kind}-type`}
              >
                {c.observationType}
              </span>
            )}
            {c.confidence && (
              <span
                className="text-[11px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-full"
                style={{ background: confidenceBg(c.confidence), color: confidenceFg(c.confidence) }}
                data-testid={`candidate-${c.kind}-confidence`}
              >
                {c.confidence}
              </span>
            )}
            <span className="ml-auto text-[11px] uppercase tracking-widest font-semibold" style={{ color: severityColor(c.severity) }}>
              {c.severity}
            </span>
          </li>
        ))}
      </ul>
    )}
  </section>
);

const ChangeLogSection = ({ log }) => {
  if (!log || log.length === 0) {
    return (
      <section
        className="mt-6 rounded-2xl border-2 bg-white overflow-hidden"
        style={{ borderColor: "var(--ta-border)" }}
      >
        <SectionHeader icon={Clock} eyebrow="History" title="Field Changes" />
        <div className="px-5 sm:px-6 py-6 text-sm" style={{ color: "var(--ta-text-secondary)" }}>
          No field changes recorded yet.
        </div>
      </section>
    );
  }
  return (
    <section
      className="mt-6 rounded-2xl border-2 bg-white overflow-hidden"
      style={{ borderColor: "var(--ta-border)" }}
      data-testid="admin-change-log"
    >
      <SectionHeader icon={Clock} eyebrow="History" title="Field Changes" />
      <ul className="divide-y" style={{ borderColor: "var(--ta-border)" }}>
        {log.map((entry) => (
          <li key={entry.id} className="px-5 sm:px-6 py-4">
            <div className="text-xs uppercase tracking-widest font-semibold flex items-center gap-2" style={{ color: "var(--ta-secondary)" }}>
              {fmtDate(entry.recordedAt)} · {entry.reason}
              <span className="text-[11px]" style={{ color: "var(--ta-text-secondary)" }}>{entry.changes.length} change(s)</span>
            </div>
            <ul className="mt-2 space-y-1">
              {entry.changes.map((c, i) => (
                <li key={i} className="text-sm font-mono" style={{ color: "var(--ta-text)" }}>
                  <span style={{ color: "var(--ta-primary)" }}>{c.field}</span>:{" "}
                  <span style={{ color: "var(--ta-text-secondary)" }}>{fmtDiffVal(c.previous)}</span>{" "}
                  <span style={{ color: "var(--ta-secondary)" }}>→</span>{" "}
                  <span>{fmtDiffVal(c.current)}</span>
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ul>
    </section>
  );
};

const _plainYesNo = (v) => {
  switch (v) {
    case "yes": return "Yes";
    case "no": return "No";
    case "partly": return "Partly";
    case "partly_accurate": return "Partly";
    case "accurate": return "Yes";
    case "inaccurate": return "No";
    case "strong": return "Strong";
    case "some": return "Some";
    case "limited": return "Limited";
    case "none_confirmed": return "None confirmed";
    case "inconclusive": return "Not enough information";
    case "unknown": return "Not enough information";
    case "high": return "High";
    case "medium": return "Medium";
    case "low": return "Low";
    default: return "Not enough information";
  }
};

const AiDiscoverySection = ({ businessId }) => {
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const loadLatest = async () => {
    setLoading(true);
    try {
      const d = await getAdminAiDiscoveryLatest(businessId);
      setSnapshot(d?.snapshot || null);
    } catch (err) {
      const msg = (err?.response?.data?.detail && (err.response.data.detail.message || err.response.data.detail))
        || err?.message || "Try again in a moment.";
      toast.error("Couldn't load AI Discovery.", { description: String(msg) });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadLatest(); }, [businessId]);  // eslint-disable-line react-hooks/exhaustive-deps

  const onConfirmRun = async () => {
    if (running) return;
    setConfirmOpen(false);
    setRunning(true);
    try {
      const d = await refreshAdminAiDiscovery(businessId);
      const snap = d?.snapshot || null;
      setSnapshot(snap);
      if (snap?.status === "complete") {
        toast.success("AI Discovery Check complete.");
      } else if (snap?.status === "partial") {
        toast.warning("AI Discovery finished with a partial result.");
      } else if (snap?.status === "failed") {
        toast.error("AI Discovery couldn't complete this time. Try again shortly.");
      } else if (snap?.status === "disabled") {
        toast.info("AI Discovery is currently disabled.");
      }
    } catch (err) {
      const msg = (err?.response?.data?.detail && (err.response.data.detail.message || err.response.data.detail))
        || err?.message || "Try again in a moment.";
      toast.error("AI Discovery request failed.", { description: String(msg) });
    } finally {
      setRunning(false);
    }
  };

  const providers = snapshot?.providers || snapshot?.probes || [];
  const summary = snapshot?.summary || {};

  return (
    <section
      data-testid="admin-ai-discovery-section"
      className="mt-6 rounded-2xl p-6"
      style={{ background: "var(--ta-surface)", border: "1px solid var(--ta-border)" }}
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2" style={{ color: "var(--ta-primary)" }}>
            <Bot size={18} strokeWidth={2.2} /> AI Discovery Check
          </h2>
          <p className="text-sm mt-1" style={{ color: "var(--ta-text-secondary)" }}>
            What ChatGPT, Gemini, and Claude currently understand about this business.
          </p>
        </div>
        <button
          type="button"
          data-testid="admin-ai-discovery-run"
          disabled={running || loading}
          onClick={() => setConfirmOpen(true)}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold border-2 transition-transform hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0"
          style={{ borderColor: "var(--ta-primary)", color: "var(--ta-primary)", background: "#fff" }}
        >
          {running ? (<><Loader2 size={14} className="animate-spin" /> Running AI Discovery Check…</>)
                   : (<><RefreshCcw size={14} strokeWidth={2.4} /> Re-run AI Discovery</>)}
        </button>
      </div>

      {loading && !snapshot && (
        <div className="mt-4 flex items-center gap-2 text-sm" style={{ color: "var(--ta-text-secondary)" }}>
          <Loader2 size={14} className="animate-spin" /> Loading…
        </div>
      )}

      {!loading && !snapshot && (
        <p className="mt-4 text-sm" style={{ color: "var(--ta-text-secondary)" }}>
          No AI Discovery Check has been run for this business yet.
        </p>
      )}

      {snapshot && (
        <div className="mt-5">
          <div className="text-xs mb-3" style={{ color: "var(--ta-text-secondary)" }}>
            Last scan: {snapshot.completedAt ? new Date(snapshot.completedAt).toLocaleString() : "in progress"}
            {" · "}status: <span className="font-semibold" style={{ color: "var(--ta-primary)" }}>{snapshot.status}</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="admin-ai-discovery-provider-grid">
            {providers.map((r) => (
              <AdminProviderCard key={r.provider} r={r} />
            ))}
          </div>
          {summary.strongestPositive && (
            <p className="mt-4 text-sm" style={{ color: "var(--ta-primary)" }} data-testid="admin-ai-strongest">
              <span className="font-semibold">Strongest positive: </span>{summary.strongestPositive}
            </p>
          )}
          {summary.mostImportantOpportunity && (
            <p className="mt-1 text-sm" style={{ color: "var(--ta-text-secondary)" }} data-testid="admin-ai-opportunity">
              <span className="font-semibold">Most important opportunity: </span>{summary.mostImportantOpportunity}
            </p>
          )}
        </div>
      )}

      {confirmOpen && (
        <ConfirmScanDialog
          onCancel={() => setConfirmOpen(false)}
          onConfirm={onConfirmRun}
        />
      )}
    </section>
  );
};

// Sprint 3D — Website Conversion Intelligence panel.
// Read-only view of the last homepage-chat-widget scan + a manual
// "Run scan" trigger. Never triggers deliveries or AI scans.
const WebsiteIntelligenceSection = ({ businessId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);

  const loadLatest = async () => {
    setLoading(true);
    try {
      const d = await getAdminWebsiteIntelligence(businessId);
      setData(d);
    } catch (err) {
      const msg = (err?.response?.data?.detail && (err.response.data.detail.message || err.response.data.detail))
        || err?.message || "Try again in a moment.";
      toast.error("Couldn't load website intelligence.", { description: String(msg) });
    } finally {
      setLoading(false);
    }
  };

  const runScan = async () => {
    if (scanning) return;
    setScanning(true);
    try {
      const d = await scanAdminWebsiteIntelligence(businessId);
      setData(d);
      const scanStatus = d?.intelligence?.websiteScanStatus;
      const status = d?.intelligence?.websiteChatStatus;
      if (scanStatus === "protected") {
        toast.info("Website security blocked the automated scan.");
      } else if (status === "present") {
        toast.success("Visitor chat widget detected.");
      } else if (status === "missing") {
        toast.info("No visitor chat widget detected.");
      } else {
        toast.warning("Homepage couldn't be scanned right now.");
      }
    } catch (err) {
      const msg = (err?.response?.data?.detail && (err.response.data.detail.message || err.response.data.detail))
        || err?.message || "Try again in a moment.";
      toast.error("Website scan failed.", { description: String(msg) });
    } finally {
      setScanning(false);
    }
  };

  useEffect(() => {
    loadLatest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId]);

  const intel = data?.intelligence;
  const website = data?.website;
  // Sprint 3F — show the URL we actually tried when the business
  // record has no canonical website but a scan was attempted.
  const attemptedUrl = intel?.attemptedUrl || intel?.sourceUrl;
  const websiteDisplay = website || attemptedUrl || null;
  // Sprint 3G — protected-site classification.
  const scanStatus = intel?.websiteScanStatus;
  const isProtected = scanStatus === "protected";
  const status = intel?.websiteChatStatus;
  const chatLabel = isProtected ? "Not enough information"
                  : status === "present" ? "Detected"
                  : status === "missing" ? "Not detected"
                  : "Unknown";
  const scanStatusLabel = scanStatus === "ok" ? "Complete"
                        : scanStatus === "protected" ? "Protected"
                        : scanStatus === "invalid_url" ? "No URL to scan"
                        : scanStatus === "error" ? "Error"
                        : "Not scanned yet";
  const reasonCopy = isProtected
    ? "Website security blocked the automated scan."
    : null;
  const statusFg = isProtected ? "var(--ta-text-secondary)"
                 : status === "present" ? "var(--ta-success)"
                 : status === "missing" ? "var(--ta-secondary)"
                 : "var(--ta-text-secondary)";
  const scanStatusFg = isProtected ? "var(--ta-warning, #b45309)"
                     : scanStatus === "ok" ? "var(--ta-success)"
                     : scanStatus === "error" || scanStatus === "invalid_url"
                       ? "var(--ta-secondary)"
                       : "var(--ta-text-secondary)";

  const scannedAt = intel?.scannedAt
    ? new Date(intel.scannedAt).toLocaleString(undefined, {
        month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
      })
    : null;

  return (
    <section
      className="mt-8 p-5 sm:p-6 rounded-2xl bg-white border"
      style={{ borderColor: "var(--ta-border)" }}
      data-testid="admin-website-intelligence-section"
    >
      <div className="flex items-center justify-between gap-3 mb-4">
        <h3 className="font-display text-lg font-semibold flex items-center gap-2" style={{ color: "var(--ta-primary)" }}>
          <Globe size={18} strokeWidth={2.2} /> Website Conversion Intelligence
        </h3>
        <button
          onClick={runScan}
          disabled={scanning}
          data-testid="admin-website-intelligence-scan"
          className="inline-flex items-center gap-2 h-9 px-4 rounded-full text-xs font-medium text-white disabled:opacity-60"
          style={{ background: "var(--ta-primary)" }}
        >
          {scanning
            ? (<><Loader2 size={14} className="animate-spin" /> Scanning…</>)
            : (<><RefreshCcw size={14} strokeWidth={2.4} /> Run homepage scan</>)}
        </button>
      </div>

      {loading && !data && (
        <div className="text-sm" style={{ color: "var(--ta-text-secondary)" }}>Loading…</div>
      )}

      {data && (
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
          <div>
            <dt className="text-[11px] uppercase tracking-widest font-semibold" style={{ color: "var(--ta-text-secondary)" }}>Website</dt>
            <dd className="mt-0.5 break-all" style={{ color: "var(--ta-primary)" }} data-testid="admin-website-intelligence-url">
              {websiteDisplay ? (
                <a href={websiteDisplay} target="_blank" rel="noreferrer" className="underline">
                  {websiteDisplay.replace(/^https?:\/\//, "")}
                </a>
              ) : (
                <span style={{ color: "var(--ta-text-secondary)" }}>No website URL available</span>
              )}
            </dd>
          </div>
          <div>
            <dt className="text-[11px] uppercase tracking-widest font-semibold" style={{ color: "var(--ta-text-secondary)" }}>Scan status</dt>
            <dd className="mt-0.5 font-semibold" style={{ color: scanStatusFg }} data-testid="admin-website-intelligence-scan-status">
              {intel ? scanStatusLabel : "Not scanned yet"}
            </dd>
          </div>
          <div>
            <dt className="text-[11px] uppercase tracking-widest font-semibold" style={{ color: "var(--ta-text-secondary)" }}>Visitor Chat</dt>
            <dd className="mt-0.5 font-semibold" style={{ color: statusFg }} data-testid="admin-website-intelligence-status">
              {intel ? chatLabel : "Not scanned yet"}
            </dd>
          </div>
          <div>
            <dt className="text-[11px] uppercase tracking-widest font-semibold" style={{ color: "var(--ta-text-secondary)" }}>Provider</dt>
            <dd className="mt-0.5" style={{ color: "var(--ta-primary)" }} data-testid="admin-website-intelligence-provider">
              {isProtected
                ? "—"
                : (intel?.providerDetected || (intel?.websiteChatStatus === "present" ? "Unrecognized widget" : "—"))}
            </dd>
          </div>
          <div>
            <dt className="text-[11px] uppercase tracking-widest font-semibold" style={{ color: "var(--ta-text-secondary)" }}>Confidence</dt>
            <dd className="mt-0.5" style={{ color: "var(--ta-primary)" }} data-testid="admin-website-intelligence-confidence">
              {isProtected ? "—" : _plainYesNo(intel?.confidence)}
            </dd>
          </div>
          <div>
            <dt className="text-[11px] uppercase tracking-widest font-semibold" style={{ color: "var(--ta-text-secondary)" }}>Source</dt>
            <dd className="mt-0.5" style={{ color: "var(--ta-primary)" }}>
              {intel?.source || "Homepage scan"}
            </dd>
          </div>
          <div>
            <dt className="text-[11px] uppercase tracking-widest font-semibold" style={{ color: "var(--ta-text-secondary)" }}>Last scanned</dt>
            <dd className="mt-0.5" style={{ color: "var(--ta-primary)" }} data-testid="admin-website-intelligence-scanned-at">
              {scannedAt || "Never"}
            </dd>
          </div>
          {isProtected && (
            <div className="sm:col-span-2">
              <dt className="text-[11px] uppercase tracking-widest font-semibold" style={{ color: "var(--ta-text-secondary)" }}>Reason</dt>
              <dd className="mt-0.5" style={{ color: "var(--ta-primary)" }} data-testid="admin-website-intelligence-reason">
                {reasonCopy}
              </dd>
            </div>
          )}
          {isProtected && intel?.possiblePlatform && (
            <>
              <div>
                <dt className="text-[11px] uppercase tracking-widest font-semibold" style={{ color: "var(--ta-text-secondary)" }}>Possible platform</dt>
                <dd className="mt-0.5" style={{ color: "var(--ta-primary)" }} data-testid="admin-website-intelligence-possible-platform">
                  {intel.possiblePlatform} infrastructure detected
                </dd>
              </div>
              <div>
                <dt className="text-[11px] uppercase tracking-widest font-semibold" style={{ color: "var(--ta-text-secondary)" }}>Platform confidence</dt>
                <dd className="mt-0.5 capitalize" style={{ color: "var(--ta-primary)" }} data-testid="admin-website-intelligence-platform-confidence">
                  {intel.possiblePlatformConfidence || "low"}
                </dd>
              </div>
            </>
          )}
          {!isProtected && intel?.error && (
            <div className="sm:col-span-2">
              <dt className="text-[11px] uppercase tracking-widest font-semibold" style={{ color: "var(--ta-text-secondary)" }}>Reason</dt>
              <dd className="mt-0.5 text-xs" style={{ color: "var(--ta-text-secondary)" }} data-testid="admin-website-intelligence-error">
                {intel.error}
              </dd>
            </div>
          )}
        </dl>
      )}
    </section>
  );
};



const AdminProviderCard = ({ r }) => {
  const failed = r?.status === "error" || r?.status === "parse_error" || r?.status === "timeout";
  return (
    <div
      data-testid={`admin-ai-provider-${r?.provider || "unknown"}`}
      className="rounded-xl p-3"
      style={{ background: "var(--ta-surface-alt)", border: "1px solid var(--ta-border)" }}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="font-semibold text-sm" style={{ color: "var(--ta-primary)" }}>
          {r?.providerDisplayName || r?.provider}
        </div>
        <div className="text-[10px] uppercase tracking-wider" style={{ color: "var(--ta-text-secondary)" }}>
          {r?.model || "—"}
        </div>
      </div>
      {failed ? (
        <div className="text-xs" style={{ color: "var(--ta-text-secondary)" }}>
          {r?.safeErrorMessage || "Unavailable — we couldn't get a clear answer this time."}
        </div>
      ) : (
        <>
          <AdminRow label="Recognized" value={r?.businessRecognized} />
          <AdminRow label="Services understood" value={r?.servicesUnderstood} />
          <AdminRow label="Website found" value={r?.websiteFound} />
          <AdminRow label="Google profile found" value={r?.googleProfileFound} />
          <AdminRow label="Review evidence" value={r?.reviewEvidence} />
          <AdminRow label="Would recommend" value={r?.recommended} />
          <AdminRow label="Confidence" value={r?.confidence} />
          {Array.isArray(r?.sourceDomains) && r.sourceDomains.length > 0 && (
            <div className="mt-2 text-[11px]" style={{ color: "var(--ta-text-secondary)" }}>
              Sources: {r.sourceDomains.join(", ")}
            </div>
          )}
        </>
      )}
    </div>
  );
};

const AdminRow = ({ label, value }) => (
  <div className="flex justify-between text-xs mb-1 last:mb-0">
    <span style={{ color: "var(--ta-text-secondary)" }}>{label}</span>
    <span className="font-medium" style={{ color: "var(--ta-primary)" }}>{_plainYesNo(value)}</span>
  </div>
);

const ConfirmScanDialog = ({ onCancel, onConfirm }) => (
  <div
    data-testid="admin-ai-discovery-confirm"
    className="fixed inset-0 z-50 flex items-center justify-center p-4"
    style={{ background: "rgba(0,0,0,0.4)" }}
    onClick={onCancel}
  >
    <div
      className="max-w-md w-full rounded-2xl p-6"
      style={{ background: "#fff", border: "1px solid var(--ta-border)" }}
      onClick={(e) => e.stopPropagation()}
    >
      <h3 className="font-semibold text-lg" style={{ color: "var(--ta-primary)" }}>
        Run a new AI Discovery Check?
      </h3>
      <p className="mt-2 text-sm" style={{ color: "var(--ta-text-secondary)" }}>
        This will run one new AI Discovery Check using ChatGPT, Gemini, and Claude.
        Three provider requests will be made.
      </p>
      <div className="mt-5 flex justify-end gap-2">
        <button
          type="button"
          data-testid="admin-ai-discovery-confirm-cancel"
          onClick={onCancel}
          className="px-4 py-2 rounded-full text-sm font-semibold"
          style={{ color: "var(--ta-primary)" }}
        >
          Cancel
        </button>
        <button
          type="button"
          data-testid="admin-ai-discovery-confirm-run"
          onClick={onConfirm}
          className="px-4 py-2 rounded-full text-sm font-semibold"
          style={{ background: "var(--ta-primary)", color: "#fff" }}
        >
          Run AI Discovery Check
        </button>
      </div>
    </div>
  </div>
);


const RawSection = ({ raw, show, onToggle }) => (
  <section
    className="mt-6 mb-16 rounded-2xl border-2 bg-white overflow-hidden"
    style={{ borderColor: "var(--ta-border)" }}
    data-testid="admin-raw-listing"
  >
    <button
      onClick={onToggle}
      className="w-full text-left px-5 sm:px-6 py-4 flex items-center justify-between"
      data-testid="admin-raw-toggle"
    >
      <div>
        <div className="text-xs uppercase tracking-widest font-semibold" style={{ color: "var(--ta-secondary)" }}>
          Provider payload
        </div>
        <div className="font-display font-semibold text-lg" style={{ color: "var(--ta-primary)" }}>
          Raw Google Data
        </div>
      </div>
      <span className="text-sm font-medium" style={{ color: "var(--ta-primary)" }}>
        {show ? "Hide" : "Show"}
      </span>
    </button>
    {show && (
      <pre
        className="px-5 sm:px-6 py-4 text-xs overflow-x-auto border-t"
        style={{ borderColor: "var(--ta-border)", background: "var(--ta-surface-alt)", color: "var(--ta-text)" }}
      >{JSON.stringify(raw ?? null, null, 2)}</pre>
    )}
  </section>
);

const SectionHeader = ({ icon: Icon, eyebrow, title, rightSlot }) => (
  <div
    className="px-5 sm:px-6 py-4 flex items-center justify-between border-b"
    style={{ borderColor: "var(--ta-border)" }}
  >
    <div className="flex items-center gap-3">
      <div
        className="w-9 h-9 rounded-xl inline-flex items-center justify-center"
        style={{ background: "var(--ta-accent)", color: "var(--ta-primary)" }}
      >
        <Icon size={18} strokeWidth={2.2} />
      </div>
      <div>
        <div className="text-[11px] uppercase tracking-widest font-semibold" style={{ color: "var(--ta-secondary)" }}>
          {eyebrow}
        </div>
        <div className="font-display font-semibold text-lg leading-tight" style={{ color: "var(--ta-primary)" }}>
          {title}
        </div>
      </div>
    </div>
    {rightSlot}
  </div>
);

const StatusPill = ({ status }) => {
  if (!status) return null;
  const ok = status.toUpperCase() === "OPERATIONAL";
  return (
    <span
      className="text-[11px] uppercase tracking-widest font-semibold px-2.5 py-1 rounded-full inline-flex items-center gap-1"
      style={{ background: ok ? "var(--ta-success-bg)" : "#F5E7DE", color: ok ? "var(--ta-success)" : "#8A4A2D" }}
      data-testid="listing-status-pill"
    >
      {ok ? <Check size={12} /> : <X size={12} />} {status}
    </span>
  );
};

const FieldLabel = ({ label, icon: Icon }) => (
  <div className="text-xs uppercase tracking-widest font-semibold mb-1 inline-flex items-center gap-1" style={{ color: "var(--ta-secondary)" }}>
    {Icon ? <Icon size={12} /> : null} {label}
  </div>
);

const Field = ({ label, value, mono, copyable, truncate, hint, testId, span = "" }) => {
  const empty = value === null || value === undefined || value === "";
  const copy = async () => {
    try { await navigator.clipboard.writeText(String(value)); toast.success("Copied"); }
    catch { toast.error("Copy failed"); }
  };
  return (
    <div className={span}>
      <FieldLabel label={label} />
      <div className="flex items-center gap-2">
        <div
          className={`text-sm ${mono ? "font-mono" : ""} ${truncate ? "truncate" : ""}`}
          style={{ color: empty ? "var(--ta-text-secondary)" : "var(--ta-text)" }}
          data-testid={testId}
        >
          {empty ? "—" : value}
        </div>
        {!empty && copyable && typeof value === "string" && (
          <button
            onClick={copy}
            className="p-1 rounded-md hover:bg-black/5"
            aria-label={`Copy ${label}`}
          >
            <Copy size={13} style={{ color: "var(--ta-text-secondary)" }} />
          </button>
        )}
      </div>
      {hint && <div className="text-xs mt-0.5 font-mono" style={{ color: "var(--ta-text-secondary)" }}>{hint}</div>}
    </div>
  );
};

const MetaField = ({ label, value, icon: Icon }) => (
  <div>
    <div className="text-[11px] uppercase tracking-widest font-semibold" style={{ color: "var(--ta-secondary)" }}>
      {label}
    </div>
    <div className="text-sm mt-0.5 inline-flex items-center gap-1" style={{ color: "var(--ta-text)" }}>
      {Icon ? <Icon size={14} style={{ color: "var(--ta-success)" }} /> : null}
      {value || "—"}
    </div>
  </div>
);

const HoursBlock = ({ hours }) => {
  const lines = hours?.weekdayDescriptions || [];
  if (!lines.length) {
    return <div className="text-sm" style={{ color: "var(--ta-text-secondary)" }}>—</div>;
  }
  return (
    <ul className="text-sm space-y-0.5" data-testid="field-hours">
      {lines.map((line, i) => (
        <li key={i} style={{ color: "var(--ta-text)" }}>{line}</li>
      ))}
    </ul>
  );
};

const SeverityDot = ({ severity }) => (
  <span
    className="w-2 h-2 rounded-full shrink-0"
    style={{ background: severityColor(severity) }}
  />
);

const PreviewSelectionPanel = ({ businessId }) => {
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);
  const load = async () => {
    setBusy(true);
    try {
      const d = await previewSelection(businessId);
      setData(d);
    } catch (e) {
      toast.error("Couldn't load preview.", { description: e?.message });
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => { load(); }, [businessId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <section
      className="mt-6 rounded-2xl border-2 bg-white overflow-hidden"
      style={{ borderColor: "var(--ta-border)" }}
      data-testid="admin-preview-selection"
    >
      <SectionHeader
        icon={Sparkles}
        eyebrow="Sprint 1E · Editorial"
        title="Preview Selection"
        rightSlot={
          data && (
            <span className="text-xs" style={{ color: "var(--ta-text-secondary)" }}>
              positive {data.counts.positive} · selected {data.counts.selected} · deferred {data.counts.deferred} · suppressed {data.counts.suppressed}
            </span>
          )
        }
      />
      {busy && !data ? (
        <div className="px-5 sm:px-6 py-6 text-sm" style={{ color: "var(--ta-text-secondary)" }}>Loading…</div>
      ) : !data ? null : (
        <div className="divide-y" style={{ borderColor: "var(--ta-border)" }}>
          <SelectionBlock title="Positive-first" testId="preview-positive" rows={data.positiveSelections.map((p) => ({
            id: p.id, code: p.observationKey || p.explanationCode, reason: p.selectionReason,
            meta: [p.attentionLevel, p.ownerValue].filter(Boolean).join(" · "),
          }))} />
          <SelectionBlock title="Attention (selected)" testId="preview-attention" rows={data.attentionSelections.map((a) => ({
            id: a.id, code: a.reasonCode, reason: a.selectionReason,
            meta: [a.recommendationPriority, a.recommendationCategory, `Impact: ${a.expectedBusinessImpact}`].filter(Boolean).join(" · "),
          }))} />
          <SelectionBlock title="Deferred" testId="preview-deferred" rows={data.deferredSelections.map((d2) => ({
            id: d2.id, code: d2.reasonCode, reason: d2.selectionReason,
            meta: [d2.recommendationPriority, `Impact: ${d2.expectedBusinessImpact}`].filter(Boolean).join(" · "),
          }))} muted />
          <SelectionBlock title="Suppressed" testId="preview-suppressed" rows={data.suppressedSelections.map((s) => ({
            id: s.id, code: s.reasonCode, reason: s.selectionReason,
            meta: s.status || "",
          }))} muted />
        </div>
      )}
    </section>
  );
};

const SelectionBlock = ({ title, testId, rows, muted }) => (
  <div className="px-5 sm:px-6 py-3" data-testid={testId}>
    <div className="text-[11px] uppercase tracking-widest font-semibold mb-2" style={{ color: "var(--ta-secondary)" }}>
      {title} · {rows.length}
    </div>
    {rows.length === 0 ? (
      <div className="text-xs" style={{ color: "var(--ta-text-secondary)" }}>—</div>
    ) : (
      <ul className="flex flex-col gap-1.5">
        {rows.map((r, i) => (
          <li
            key={r.id || `${r.code}-${i}`}
            className="text-sm flex flex-wrap items-center gap-2"
            style={{ opacity: muted ? 0.7 : 1 }}
            data-testid={`${testId}-row-${r.code}`}
          >
            <code className="font-mono text-[13px]" style={{ color: "var(--ta-primary)" }}>{r.code}</code>
            <span
              className="text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-full"
              style={{ background: "var(--ta-surface-alt)", color: "var(--ta-primary)" }}
            >
              {r.reason}
            </span>
            {r.meta && (
              <span className="text-xs" style={{ color: "var(--ta-text-secondary)" }}>· {r.meta}</span>
            )}
          </li>
        ))}
      </ul>
    )}
  </div>
);


const severityColor = (s) => {
  if (s === "positive") return "var(--ta-success)";
  if (s === "attention") return "#C2571B";
  return "var(--ta-secondary)";
};

const confidenceBg = (c) => {
  if (c === "high") return "var(--ta-success-bg)";
  if (c === "medium") return "var(--ta-accent)";
  if (c === "low") return "#F5F1EA";
  return "var(--ta-surface-alt)";
};

const confidenceFg = (c) => {
  if (c === "high") return "var(--ta-success)";
  if (c === "medium") return "var(--ta-primary)";
  if (c === "low") return "#7A6A56";
  return "var(--ta-text-secondary)";
};

const humanize = (t) => (t || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

const fmtDiffVal = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  if (Array.isArray(v)) return `[${v.join(", ")}]`;
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
};


// ---------------------------------------------------------------------------
// Sprint 1F — Brief Composition (Draft Preview + Diagnostic View)
// ---------------------------------------------------------------------------

const BriefCompositionSection = ({ businessId }) => {
  const [loading, setLoading] = useState(false);
  const [composing, setComposing] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [brief, setBrief] = useState(null);
  const [selection, setSelection] = useState(null);
  const [mentions, setMentions] = useState([]);
  const [view, setView] = useState("preview"); // "preview" | "diagnostic"

  const loadLatest = async () => {
    setLoading(true);
    try {
      const { brief: b } = await getLatestBriefDraft(businessId);
      if (b?.id) {
        const full = await getBriefComposition(b.id);
        setBrief(full.brief);
        setSelection(full.selection);
        setMentions(full.mentions || []);
      } else {
        setBrief(null);
        setSelection(null);
        setMentions([]);
      }
    } catch (e) {
      toast.error("Couldn't load brief draft.", { description: e?.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadLatest(); }, [businessId]); // eslint-disable-line react-hooks/exhaustive-deps

  const onCompose = async () => {
    setComposing(true);
    try {
      const { brief: b } = await composeBriefDraft(businessId);
      toast.success("Draft composed.", { description: `Frame: ${b.frameKey}` });
      const full = await getBriefComposition(b.id);
      setBrief(full.brief);
      setSelection(full.selection);
      setMentions(full.mentions || []);
    } catch (e) {
      toast.error("Compose failed.", { description: formatApiErrorForToast(e) });
    } finally {
      setComposing(false);
    }
  };

  const onRegenerate = async () => {
    if (!brief?.id) return;
    setRegenerating(true);
    try {
      const { brief: b } = await regenerateBriefComposition(brief.id);
      toast.success("Draft regenerated from same selection.", {
        description: `selectionId ${b.selectionId?.slice(0, 8)}…`,
      });
      const full = await getBriefComposition(b.id);
      setBrief(full.brief);
      setSelection(full.selection);
      setMentions(full.mentions || []);
    } catch (e) {
      toast.error("Regenerate failed.", { description: formatApiErrorForToast(e) });
    } finally {
      setRegenerating(false);
    }
  };

  return (
    <section
      className="mt-6 rounded-2xl border-2 bg-white overflow-hidden"
      style={{ borderColor: "var(--ta-border)" }}
      data-testid="admin-brief-composition"
    >
      <SectionHeader
        icon={FileText}
        eyebrow="Sprint 1F · Composition"
        title="Monday Brief Draft"
        rightSlot={
          <div className="flex items-center gap-2">
            {brief && (
              <span className="text-xs" style={{ color: "var(--ta-text-secondary)" }}>
                v{brief.compositionVersion} · frame {brief.frameKey}
              </span>
            )}
            <button
              type="button"
              onClick={onCompose}
              disabled={composing}
              className="text-xs font-semibold px-3 py-1.5 rounded-full inline-flex items-center gap-1.5 disabled:opacity-50"
              style={{ background: "var(--ta-primary)", color: "white" }}
              data-testid="brief-compose-new-btn"
            >
              {composing ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
              {composing ? "Composing…" : brief ? "New draft" : "Compose draft"}
            </button>
            {brief && (
              <button
                type="button"
                onClick={onRegenerate}
                disabled={regenerating}
                className="text-xs font-semibold px-3 py-1.5 rounded-full inline-flex items-center gap-1.5 disabled:opacity-50"
                style={{ background: "var(--ta-surface-alt)", color: "var(--ta-primary)" }}
                data-testid="brief-regenerate-btn"
              >
                {regenerating ? <Loader2 size={12} className="animate-spin" /> : <RefreshCcw size={12} />}
                {regenerating ? "Regenerating…" : "Regenerate"}
              </button>
            )}
          </div>
        }
      />

      {loading && !brief ? (
        <div className="px-5 sm:px-6 py-6 text-sm" style={{ color: "var(--ta-text-secondary)" }}>
          Loading…
        </div>
      ) : !brief ? (
        <div className="px-5 sm:px-6 py-6 text-sm" style={{ color: "var(--ta-text-secondary)" }} data-testid="brief-composition-empty">
          No draft yet. Click <strong>Compose draft</strong> to freeze the current selection and generate a customer-facing draft.
        </div>
      ) : (
        <>
          {/* View toggle */}
          <div className="px-5 sm:px-6 pt-3 pb-1 flex items-center gap-2 border-b" style={{ borderColor: "var(--ta-border)" }}>
            <ViewToggleTab active={view === "preview"} onClick={() => setView("preview")} testId="brief-view-preview">
              Draft Preview
            </ViewToggleTab>
            <ViewToggleTab active={view === "diagnostic"} onClick={() => setView("diagnostic")} testId="brief-view-diagnostic">
              Diagnostic View
            </ViewToggleTab>
          </div>

          {view === "preview" ? (
            <BriefDraftPreview brief={brief} />
          ) : (
            <BriefDiagnosticView brief={brief} selection={selection} mentions={mentions} />
          )}
        </>
      )}
    </section>
  );
};

const ViewToggleTab = ({ active, onClick, testId, children }) => (
  <button
    type="button"
    onClick={onClick}
    className="text-xs font-semibold px-3 py-1.5 rounded-full inline-flex items-center gap-1.5"
    style={{
      background: active ? "var(--ta-primary)" : "transparent",
      color: active ? "white" : "var(--ta-primary)",
      border: `1px solid ${active ? "var(--ta-primary)" : "var(--ta-border)"}`,
    }}
    data-testid={testId}
  >
    {children}
  </button>
);

const BriefDraftPreview = ({ brief }) => {
  return (
    <div className="px-5 sm:px-6 py-6" data-testid="brief-draft-preview">
      {/* Email meta */}
      <div className="mb-6 pb-4 border-b" style={{ borderColor: "var(--ta-border)" }}>
        <div className="text-[10px] uppercase tracking-widest font-semibold mb-1" style={{ color: "var(--ta-secondary)" }}>
          Subject
        </div>
        <div className="text-sm font-medium" style={{ color: "var(--ta-primary)" }} data-testid="brief-draft-subject">
          {brief.subject}
        </div>
        <div className="text-[10px] uppercase tracking-widest font-semibold mt-3 mb-1" style={{ color: "var(--ta-secondary)" }}>
          Preview text
        </div>
        <div className="text-xs" style={{ color: "var(--ta-text-secondary)" }} data-testid="brief-draft-preview-text">
          {brief.previewText}
        </div>
      </div>

      {/* Greeting + opening */}
      <div className="mb-6">
        <h3 className="text-lg font-serif mb-3" style={{ color: "var(--ta-primary)" }} data-testid="brief-draft-greeting">
          {brief.greeting}
        </h3>
        <p className="text-sm leading-relaxed" style={{ color: "var(--ta-text)" }} data-testid="brief-draft-opening">
          {brief.openingSummary}
        </p>
      </div>

      {/* Positive items */}
      {brief.positiveItems?.length > 0 && (
        <div className="mb-6">
          <div className="text-[10px] uppercase tracking-widest font-semibold mb-3" style={{ color: "var(--ta-accent-dark, #C2571B)" }}>
            Here's what went well
          </div>
          <ul className="flex flex-col gap-3" data-testid="brief-positive-items">
            {brief.positiveItems.map((p, i) => (
              <li
                key={p.diagnostic.itemId || i}
                className="rounded-xl p-4"
                style={{ background: "var(--ta-surface-alt)" }}
                data-testid={`brief-positive-item-${i}`}
              >
                <div className="text-sm font-semibold mb-1" style={{ color: "var(--ta-primary)" }}>
                  {p.customerFacing.headline}
                </div>
                <div className="text-sm leading-relaxed" style={{ color: "var(--ta-text)" }}>
                  {p.customerFacing.message}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Attention items */}
      {brief.attentionItems?.length > 0 && (
        <div className="mb-6">
          <div className="text-[10px] uppercase tracking-widest font-semibold mb-3" style={{ color: "var(--ta-accent-dark, #C2571B)" }}>
            Here's what caught our attention
          </div>
          <ul className="flex flex-col gap-4" data-testid="brief-attention-items">
            {brief.attentionItems.map((a, i) => (
              <li
                key={a.diagnostic.itemId || i}
                className="rounded-xl p-4 border"
                style={{ borderColor: "var(--ta-border)", background: "white" }}
                data-testid={`brief-attention-item-${i}`}
              >
                <div className="text-base font-semibold mb-2" style={{ color: "var(--ta-primary)" }}>
                  {a.customerFacing.headline}
                </div>
                <AttnField label="What we noticed" value={a.customerFacing.whatWeNoticed} />
                <AttnField label="Why it matters" value={a.customerFacing.whyItMatters} />
                <AttnField label="What we recommend" value={a.customerFacing.recommendedAction} />
                <div className="flex flex-wrap gap-2 mt-3">
                  {a.customerFacing.ownerCanHandle && (
                    <span
                      className="text-[11px] font-medium px-3 py-1.5 rounded-full"
                      style={{ background: "white", border: "1.5px solid var(--ta-primary)", color: "var(--ta-primary)" }}
                    >
                      {a.customerFacing.ownerActionLabel}
                    </span>
                  )}
                  {a.customerFacing.growthPartnerCanHandle && (
                    <span
                      className="text-[11px] font-medium px-3 py-1.5 rounded-full"
                      style={{ background: "var(--ta-accent, #D96C51)", color: "white" }}
                    >
                      {a.customerFacing.growthPartnerActionLabel}
                    </span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Closing */}
      <div className="mt-6 rounded-xl p-4" style={{ background: "var(--ta-success-bg, #E5F0EB)", color: "var(--ta-success, #2E7D5C)" }}>
        <p className="text-sm leading-relaxed" data-testid="brief-draft-closing">
          {brief.closing}
        </p>
      </div>
    </div>
  );
};

const AttnField = ({ label, value }) => (
  <div className="mt-2">
    <div className="text-[10px] uppercase tracking-widest font-semibold mb-0.5" style={{ color: "var(--ta-secondary)" }}>
      {label}
    </div>
    <div className="text-sm leading-relaxed" style={{ color: "var(--ta-text)" }}>
      {value}
    </div>
  </div>
);

const BriefDiagnosticView = ({ brief, selection, mentions }) => {
  const unresolved = brief.diagnostic?.unresolvedPlaceholders || [];
  return (
    <div className="px-5 sm:px-6 py-6" data-testid="brief-diagnostic-view">
      <DiagBlock title="Brief" testId="diag-brief">
        <KV k="briefId" v={brief.id} />
        <KV k="compositionKind" v={brief.compositionKind} />
        <KV k="compositionVersion" v={brief.compositionVersion} />
        <KV k="templateRegistryFingerprint" v={(brief.templateRegistryFingerprint || "").slice(0, 16) + "…"} />
        <KV k="selectionId" v={brief.selectionId} />
        <KV k="selectionRuleVersion" v={brief.selectionRuleVersion} />
        <KV k="frameKey" v={brief.frameKey} />
        <KV k="greetingKey" v={brief.greetingKey} />
        <KV k="composedAt" v={brief.composedAt} />
        <KV k="status" v={brief.status} />
      </DiagBlock>

      <DiagBlock
        title={`Unresolved placeholders (${unresolved.length})`}
        testId="diag-placeholders"
        tone={unresolved.length > 0 ? "warn" : "ok"}
      >
        {unresolved.length === 0 ? (
          <div className="text-xs" style={{ color: "var(--ta-success, #2E7D5C)" }}>
            All placeholders resolved.
          </div>
        ) : (
          <ul className="text-xs font-mono flex flex-wrap gap-2">
            {unresolved.map((t) => (
              <li key={t} className="px-2 py-0.5 rounded-full" style={{ background: "#F5D9CC", color: "#7A2E1E" }}>
                {t}
              </li>
            ))}
          </ul>
        )}
      </DiagBlock>

      <DiagBlock title={`Positive items (${brief.positiveItems?.length || 0})`} testId="diag-positive-items">
        <ul className="flex flex-col gap-1.5">
          {(brief.positiveItems || []).map((p, i) => (
            <li key={p.diagnostic.itemId || i} className="text-xs flex flex-wrap items-center gap-2" data-testid={`diag-positive-row-${p.diagnostic.explanationCode}`}>
              <code className="font-mono text-[13px]" style={{ color: "var(--ta-primary)" }}>{p.diagnostic.explanationCode}</code>
              <span className="text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-full" style={{ background: "var(--ta-surface-alt)", color: "var(--ta-primary)" }}>
                {p.diagnostic.selectionReasonCode}
              </span>
              <span style={{ color: "var(--ta-text-secondary)" }}>rank {p.diagnostic.selectionRank}</span>
              <span style={{ color: "var(--ta-text-secondary)" }}>· template {p.diagnostic.templateKey}@{p.diagnostic.templateVersion}</span>
            </li>
          ))}
        </ul>
      </DiagBlock>

      <DiagBlock title={`Attention items (${brief.attentionItems?.length || 0})`} testId="diag-attention-items">
        <ul className="flex flex-col gap-1.5">
          {(brief.attentionItems || []).map((a, i) => (
            <li key={a.diagnostic.itemId || i} className="text-xs flex flex-wrap items-center gap-2" data-testid={`diag-attention-row-${a.diagnostic.reasonCode}`}>
              <code className="font-mono text-[13px]" style={{ color: "var(--ta-primary)" }}>{a.diagnostic.reasonCode}</code>
              <span className="text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-full" style={{ background: "var(--ta-surface-alt)", color: "var(--ta-primary)" }}>
                {a.diagnostic.selectionReasonCode}
              </span>
              <span style={{ color: "var(--ta-text-secondary)" }}>rank {a.diagnostic.selectionRank}</span>
              <span style={{ color: "var(--ta-text-secondary)" }}>· template {a.diagnostic.templateKey}@{a.diagnostic.templateVersion}</span>
            </li>
          ))}
        </ul>
      </DiagBlock>

      <DiagBlock title={`Mentions (${mentions.length})`} testId="diag-mentions">
        <ul className="flex flex-col gap-1.5">
          {mentions.map((m) => (
            <li key={m.id} className="text-xs flex flex-wrap items-center gap-2">
              <code className="font-mono text-[12px]" style={{ color: "var(--ta-primary)" }}>
                {m.sourceType}·{m.explanationCode || m.reasonCode || m.sourceId.slice(0, 8)}
              </code>
              <MentionFlag label="wasSelected" v={m.wasSelected} />
              <MentionFlag label="wasComposed" v={m.wasComposed} />
              <MentionFlag label="wasShown" v={m.wasShown} />
            </li>
          ))}
        </ul>
      </DiagBlock>

      {selection && (
        <DiagBlock title="Frozen selection snapshot" testId="diag-selection">
          <KV k="selectionId" v={selection.id} />
          <KV k="selectionRuleVersion" v={selection.selectionRuleVersion} />
          <KV k="selectionFingerprint" v={(selection.selectionFingerprint || "").slice(0, 16) + "…"} />
          <KV k="createdAt" v={selection.createdAt} />
          <KV k="counts" v={JSON.stringify(selection.counts)} />
          <KV k="immutable" v={String(selection.immutable)} />
        </DiagBlock>
      )}
    </div>
  );
};

const DiagBlock = ({ title, children, testId, tone }) => (
  <div
    className="mb-4 rounded-xl p-3 border"
    style={{
      borderColor: tone === "warn" ? "#E9A388" : "var(--ta-border)",
      background: tone === "warn" ? "#FBEDE5" : "var(--ta-surface, white)",
    }}
    data-testid={testId}
  >
    <div className="text-[10px] uppercase tracking-widest font-semibold mb-2" style={{ color: "var(--ta-secondary)" }}>
      {title}
    </div>
    {children}
  </div>
);

const KV = ({ k, v }) => (
  <div className="text-xs flex gap-2 items-baseline">
    <span className="font-mono" style={{ color: "var(--ta-text-secondary)" }}>{k}:</span>
    <span className="font-mono" style={{ color: "var(--ta-primary)" }}>{String(v ?? "—")}</span>
  </div>
);

const MentionFlag = ({ label, v }) => (
  <span
    className="text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-full"
    style={{
      background: v ? "var(--ta-success-bg, #E5F0EB)" : "var(--ta-surface-alt)",
      color: v ? "var(--ta-success, #2E7D5C)" : "var(--ta-text-secondary)",
    }}
  >
    {label}:{v ? "yes" : "no"}
  </span>
);

// ---------------------------------------------------------------------------
// Sprint 1G — Rendering & Delivery
// ---------------------------------------------------------------------------

const BriefDeliverySection = ({ businessId }) => {
  const [brief, setBrief] = useState(null);
  const [renderings, setRenderings] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [activeRendering, setActiveRendering] = useState(null);
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(false);
  const [previewMode, setPreviewMode] = useState("html"); // "html" | "plain"
  const [testRecipient, setTestRecipient] = useState("");
  const [openEvents, setOpenEvents] = useState(null);

  const loadAll = async () => {
    try {
      const draft = await getLatestBriefDraft(businessId);
      const b = draft?.brief;
      setBrief(b || null);
      if (!b?.id) { setRenderings([]); setDeliveries([]); setActiveRendering(null); return; }
      const [rs, ds] = await Promise.all([
        listBriefRenderings(b.id).then((r) => r.renderings || []),
        listBriefRenderingDeliveries(b.id).then((r) => r.deliveries || []),
      ]);
      setRenderings(rs);
      setDeliveries(ds);
      setActiveRendering(rs.find((r) => r.id === b.approvedRenderingId) || rs[0] || null);
    } catch (e) {
      toast.error("Couldn't load delivery data.", { description: e?.message });
    }
  };

  useEffect(() => { loadAll(); }, [businessId]); // eslint-disable-line react-hooks/exhaustive-deps

  const onCreate = async () => {
    if (!brief?.id) return;
    setCreating(true);
    try {
      const { rendering } = await createBriefRendering(brief.id);
      toast.success("Rendering created.", { description: `fingerprint ${rendering.contentFingerprint.slice(0, 12)}…` });
      await loadAll();
    } catch (e) {
      toast.error("Render failed.", { description: formatApiErrorForToast(e) });
    } finally {
      setCreating(false);
    }
  };

  const onApprove = async (renderingId) => {
    setBusy(true);
    try {
      await approveBriefRendering(renderingId);
      toast.success("Rendering approved.");
      await loadAll();
    } catch (e) {
      toast.error("Approve failed.", { description: formatApiErrorForToast(e) });
    } finally { setBusy(false); }
  };

  const onSendTest = async () => {
    if (!activeRendering?.id) return;
    if (!testRecipient) { toast.error("Enter a test recipient email."); return; }
    setBusy(true);
    try {
      await sendTestBriefRendering(activeRendering.id, testRecipient.trim());
      toast.success("Test send submitted.", { description: `To ${testRecipient.trim()}` });
      await loadAll();
    } catch (e) {
      toast.error("Test send failed.", { description: formatApiErrorForToast(e) });
    } finally { setBusy(false); }
  };

  const onSendNow = async () => {
    if (!activeRendering?.id) return;
    if (!window.confirm("Send this brief to the customer right now?")) return;
    setBusy(true);
    try {
      await sendNowBriefRendering(activeRendering.id);
      toast.success("Production send submitted.");
      await loadAll();
    } catch (e) {
      toast.error("Send failed.", { description: formatApiErrorForToast(e) });
    } finally { setBusy(false); }
  };

  const onSchedule = async () => {
    if (!activeRendering?.id) return;
    setBusy(true);
    try {
      const { delivery } = await scheduleBriefRendering(activeRendering.id, null);
      toast.success("Scheduled.", { description: `Scheduled for ${delivery.scheduledFor}` });
      await loadAll();
    } catch (e) {
      toast.error("Schedule failed.", { description: formatApiErrorForToast(e) });
    } finally { setBusy(false); }
  };

  const onCancel = async (deliveryId) => {
    setBusy(true);
    try {
      await cancelDelivery(deliveryId);
      toast.success("Delivery canceled.");
      await loadAll();
    } catch (e) {
      toast.error("Cancel failed.", { description: formatApiErrorForToast(e) });
    } finally { setBusy(false); }
  };

  const onRetry = async (deliveryId) => {
    setBusy(true);
    try {
      await retryDeliveryV2(deliveryId);
      toast.success("Retry queued.");
      await loadAll();
    } catch (e) {
      toast.error("Retry failed.", { description: formatApiErrorForToast(e) });
    } finally { setBusy(false); }
  };

  const onViewEvents = async (deliveryId) => {
    try {
      const { events } = await listDeliveryEvents(deliveryId);
      setOpenEvents({ deliveryId, events });
    } catch (e) {
      toast.error("Couldn't load events.", { description: e?.message });
    }
  };

  return (
    <section
      className="mt-6 rounded-2xl border-2 bg-white overflow-hidden"
      style={{ borderColor: "var(--ta-border)" }}
      data-testid="admin-brief-delivery"
    >
      <SectionHeader
        icon={FileText}
        eyebrow="Sprint 1G · Rendering & Delivery"
        title="Email delivery"
        rightSlot={
          brief?.id ? (
            <button
              type="button"
              onClick={onCreate}
              disabled={creating}
              className="text-xs font-semibold px-3 py-1.5 rounded-full inline-flex items-center gap-1.5 disabled:opacity-50"
              style={{ background: "var(--ta-primary)", color: "white" }}
              data-testid="delivery-create-rendering-btn"
            >
              {creating ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
              {creating ? "Rendering…" : "Create rendering"}
            </button>
          ) : null
        }
      />

      {!brief?.id ? (
        <div className="px-5 sm:px-6 py-6 text-sm" style={{ color: "var(--ta-text-secondary)" }}>
          Compose a draft first, then create a rendering to send it.
        </div>
      ) : renderings.length === 0 ? (
        <div className="px-5 sm:px-6 py-6 text-sm" style={{ color: "var(--ta-text-secondary)" }} data-testid="delivery-no-renderings">
          No rendering yet. Click <strong>Create rendering</strong> to generate one.
        </div>
      ) : (
        <>
          <RenderingsList
            renderings={renderings}
            activeId={activeRendering?.id}
            approvedId={brief.approvedRenderingId}
            onSelect={(r) => setActiveRendering(r)}
            onApprove={onApprove}
            busy={busy}
          />

          {activeRendering && (
            <>
              <div className="px-5 sm:px-6 py-3 flex flex-wrap items-center gap-2 border-t" style={{ borderColor: "var(--ta-border)" }}>
                <ViewToggleTab active={previewMode === "html"} onClick={() => setPreviewMode("html")} testId="delivery-preview-html">HTML preview</ViewToggleTab>
                <ViewToggleTab active={previewMode === "plain"} onClick={() => setPreviewMode("plain")} testId="delivery-preview-plain">Plain text</ViewToggleTab>
                <div className="flex-1" />
                <div className="text-[11px] font-mono" style={{ color: "var(--ta-text-secondary)" }}>
                  fingerprint {activeRendering.contentFingerprint.slice(0, 16)}… · v{activeRendering.rendererVersion}
                </div>
              </div>
              {previewMode === "html" ? (
                <iframe
                  title="brief-html-preview"
                  srcDoc={activeRendering.html}
                  sandbox=""
                  style={{ width: "100%", minHeight: 640, border: 0 }}
                  data-testid="delivery-html-iframe"
                />
              ) : (
                <pre
                  className="px-5 sm:px-6 py-4 text-xs whitespace-pre-wrap"
                  style={{ color: "var(--ta-text)", background: "var(--ta-surface-alt)", fontFamily: "SFMono-Regular, monospace" }}
                  data-testid="delivery-plain-pre"
                >
                  {activeRendering.plainText}
                </pre>
              )}

              <div className="px-5 sm:px-6 py-4 border-t flex flex-wrap items-center gap-2" style={{ borderColor: "var(--ta-border)" }}>
                <input
                  type="email"
                  placeholder="qa@example.com"
                  value={testRecipient}
                  onChange={(e) => setTestRecipient(e.target.value)}
                  className="text-sm px-3 py-1.5 rounded-full flex-1 min-w-[200px]"
                  style={{ background: "var(--ta-surface-alt)", border: "1px solid var(--ta-border)" }}
                  data-testid="delivery-test-recipient-input"
                />
                <button
                  type="button"
                  onClick={onSendTest}
                  disabled={busy}
                  className="text-xs font-semibold px-3 py-1.5 rounded-full inline-flex items-center gap-1.5 disabled:opacity-50"
                  style={{ background: "var(--ta-surface-alt)", color: "var(--ta-primary)", border: "1.5px solid var(--ta-primary)" }}
                  data-testid="delivery-send-test-btn"
                >
                  Send test
                </button>
                <button
                  type="button"
                  onClick={onSchedule}
                  disabled={busy || !activeRendering.approvedAt}
                  className="text-xs font-semibold px-3 py-1.5 rounded-full inline-flex items-center gap-1.5 disabled:opacity-50"
                  style={{ background: "var(--ta-surface-alt)", color: "var(--ta-primary)", border: "1.5px solid var(--ta-primary)" }}
                  data-testid="delivery-schedule-btn"
                >
                  Schedule (next Monday 7am)
                </button>
                <button
                  type="button"
                  onClick={onSendNow}
                  disabled={busy || !activeRendering.approvedAt}
                  className="text-xs font-semibold px-3 py-1.5 rounded-full inline-flex items-center gap-1.5 disabled:opacity-50"
                  style={{ background: "var(--ta-accent, #D96C51)", color: "white" }}
                  data-testid="delivery-send-now-btn"
                  title={activeRendering.approvedAt ? "" : "Approve this rendering first"}
                >
                  Send now
                </button>
              </div>
            </>
          )}

          <DeliveryTable
            deliveries={deliveries}
            onCancel={onCancel}
            onRetry={onRetry}
            onViewEvents={onViewEvents}
            busy={busy}
          />

          {openEvents && (
            <div className="px-5 sm:px-6 py-4 border-t" style={{ borderColor: "var(--ta-border)" }} data-testid="delivery-event-timeline">
              <div className="text-[10px] uppercase tracking-widest font-semibold mb-2" style={{ color: "var(--ta-secondary)" }}>
                Event timeline · delivery {openEvents.deliveryId.slice(0, 8)}…
              </div>
              {openEvents.events.length === 0 ? (
                <div className="text-xs" style={{ color: "var(--ta-text-secondary)" }}>No verified events yet.</div>
              ) : (
                <ul className="flex flex-col gap-2">
                  {openEvents.events.map((e) => (
                    <li key={e.webhookEventId} className="text-xs flex flex-wrap items-center gap-2">
                      <code className="font-mono" style={{ color: "var(--ta-primary)" }}>{e.eventType}</code>
                      <span style={{ color: "var(--ta-text-secondary)" }}>{e.eventCreatedAt}</span>
                      <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: "var(--ta-surface-alt)" }}>
                        {e.processingStatus || "received"}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
              <button type="button" onClick={() => setOpenEvents(null)} className="mt-2 text-xs underline" style={{ color: "var(--ta-primary)" }}>
                Hide events
              </button>
            </div>
          )}
        </>
      )}
    </section>
  );
};

const RenderingsList = ({ renderings, activeId, approvedId, onSelect, onApprove, busy }) => (
  <div className="px-5 sm:px-6 py-3 flex flex-col gap-2 border-b" style={{ borderColor: "var(--ta-border)" }} data-testid="delivery-renderings-list">
    <div className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: "var(--ta-secondary)" }}>
      Renderings ({renderings.length})
    </div>
    {renderings.map((r) => {
      const isActive = r.id === activeId;
      const isApproved = r.id === approvedId;
      return (
        <div
          key={r.id}
          className="flex flex-wrap items-center gap-2 rounded-lg px-3 py-2"
          style={{
            background: isActive ? "var(--ta-surface-alt)" : "transparent",
            border: `1px solid ${isActive ? "var(--ta-primary)" : "var(--ta-border)"}`,
          }}
          data-testid={`delivery-rendering-row-${r.id}`}
        >
          <button
            type="button"
            onClick={() => onSelect(r)}
            className="text-xs font-mono text-left flex-1"
            style={{ color: "var(--ta-primary)" }}
          >
            {r.id.slice(0, 8)}… · v{r.rendererVersion} · fp {r.contentFingerprint.slice(0, 12)}…
          </button>
          <span className="text-[11px]" style={{ color: "var(--ta-text-secondary)" }}>
            {new Date(r.createdAt).toLocaleString()}
          </span>
          {isApproved ? (
            <span className="text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-full" style={{ background: "var(--ta-success-bg, #E5F0EB)", color: "var(--ta-success, #2E7D5C)" }}>
              Approved
            </span>
          ) : (
            <button
              type="button"
              onClick={() => onApprove(r.id)}
              disabled={busy}
              className="text-[11px] font-semibold px-2 py-1 rounded-full"
              style={{ background: "var(--ta-accent, #D96C51)", color: "white" }}
              data-testid={`delivery-approve-btn-${r.id}`}
            >
              Approve
            </button>
          )}
        </div>
      );
    })}
  </div>
);

const DeliveryTable = ({ deliveries, onCancel, onRetry, onViewEvents, busy }) => (
  <div className="px-5 sm:px-6 py-4 border-t" style={{ borderColor: "var(--ta-border)" }} data-testid="delivery-table">
    <div className="text-[10px] uppercase tracking-widest font-semibold mb-2" style={{ color: "var(--ta-secondary)" }}>
      Deliveries ({deliveries.length})
    </div>
    {deliveries.length === 0 ? (
      <div className="text-xs" style={{ color: "var(--ta-text-secondary)" }}>No deliveries yet.</div>
    ) : (
      <ul className="flex flex-col gap-2">
        {deliveries.map((d) => (
          <li
            key={d.id}
            className="rounded-lg px-3 py-2 flex flex-wrap items-center gap-2"
            style={{ border: "1px solid var(--ta-border)" }}
            data-testid={`delivery-row-${d.id}`}
          >
            <code className="text-xs font-mono" style={{ color: "var(--ta-primary)" }}>{d.id.slice(0, 8)}…</code>
            <span className="text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-full" style={{ background: "var(--ta-surface-alt)", color: "var(--ta-primary)" }}>
              {d.sendType}
            </span>
            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{ background: statusColor(d.deliveryStatus).bg, color: statusColor(d.deliveryStatus).ink }}>
              {d.deliveryStatus}
            </span>
            <span className="text-xs" style={{ color: "var(--ta-text-secondary)" }}>→ {maskEmail(d.recipient)}</span>
            {d.providerMessageId && (
              <span className="text-[10px] font-mono" style={{ color: "var(--ta-text-secondary)" }}>
                pmid {d.providerMessageId.slice(0, 10)}…
              </span>
            )}
            <div className="flex-1" />
            <button
              type="button"
              onClick={() => onViewEvents(d.id)}
              className="text-[11px] underline"
              style={{ color: "var(--ta-primary)" }}
              data-testid={`delivery-view-events-${d.id}`}
            >
              Events
            </button>
            {["Prepared", "Scheduled"].includes(d.deliveryStatus) && (
              <button
                type="button"
                onClick={() => onCancel(d.id)}
                disabled={busy}
                className="text-[11px] underline"
                style={{ color: "var(--ta-accent-dark, #C2571B)" }}
                data-testid={`delivery-cancel-${d.id}`}
              >
                Cancel
              </button>
            )}
            {["Failed", "Delivery Delayed"].includes(d.deliveryStatus) && (
              <button
                type="button"
                onClick={() => onRetry(d.id)}
                disabled={busy}
                className="text-[11px] underline"
                style={{ color: "var(--ta-primary)" }}
                data-testid={`delivery-retry-${d.id}`}
              >
                Retry
              </button>
            )}
          </li>
        ))}
      </ul>
    )}
  </div>
);

const statusColor = (status) => {
  const map = {
    Prepared:           { bg: "#F3F4F6", ink: "#374151" },
    Scheduled:          { bg: "#DBEAFE", ink: "#1E3A8A" },
    Submitting:         { bg: "#DBEAFE", ink: "#1E3A8A" },
    Submitted:          { bg: "#DBEAFE", ink: "#1E3A8A" },
    Sent:               { bg: "#FEF3C7", ink: "#78350F" },
    Delivered:          { bg: "#D1FAE5", ink: "#065F46" },
    "Delivery Delayed": { bg: "#FEF3C7", ink: "#78350F" },
    Failed:             { bg: "#FEE2E2", ink: "#7F1D1D" },
    Bounced:            { bg: "#FEE2E2", ink: "#7F1D1D" },
    Suppressed:         { bg: "#FEE2E2", ink: "#7F1D1D" },
    Complained:         { bg: "#FCE7F3", ink: "#831843" },
    Canceled:           { bg: "#E5E7EB", ink: "#4B5563" },
  };
  return map[status] || { bg: "#E5E7EB", ink: "#4B5563" };
};

const maskEmail = (email) => {
  if (!email || !email.includes("@")) return email || "";
  const [name, domain] = email.split("@");
  const shown = name.length <= 2 ? name : name[0] + "…" + name[name.length - 1];
  return `${shown}@${domain}`;
};

